import { useEffect, useMemo, useState, type ReactNode } from "react";

import clsx from "clsx";
import { groupBy, isEqual, orderBy } from "lodash";

import { useSalesBreakdownConfigs } from "@components/data/sales-breakdown-configs";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { PrimaryButton, TertiaryButton } from "@components/v2/buttons";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { ordinal } from "@features/data-export/components/schedule-configurator/utils";
import {
  useBreakdownDeactivatedCheck,
  useOverviewContext,
} from "@features/overview-v2/hooks";
import {
  TileType,
  type Level,
  type TileFilter,
} from "@features/overview-v2/types";
import {
  getConversionLagMetrics,
  getExcludedFrequentlyUsedMetrics,
  getFrequentlyUsedMetrics,
  isConversionLagMetric,
} from "@features/overview-v2/utils";
import { FilterButtonActive } from "@features/sales-v3/top-level-filters/components/filter-button-active";
import { FilterButtonSelect } from "@features/sales-v3/top-level-filters/components/filter-button-select";
import { CustomMetricSearch } from "@features/sales-v3/utils/frequently-used-metrics";
import { Modal } from "@shared/modal";
import { SelectWithGroupedOptions } from "@shared/selects";

import { BreakdownsDeactivatedMessage } from "../../tile/breakdowns-deactivated-message";
import { LEVEL_TO_NUMBER, NUMBER_TO_LEVEL } from "../../tile/utils";

import { BreakdownFilter } from "./breakdown-filter";
import { BreakdownSelect } from "./breakdown-select";
import { NumberOfMetricsSwitch } from "./number-of-metrics-switch";
import { TileTypeButton } from "./tile-type-button";

/* TODO: All implementations related to managing the form values should be encapsulated in a form,
   such as react-hook-form. */
export enum NumberOfMetrics {
  ONE_METRIC = "1-metric",
  TWO_METRICS = "2-metrics",
}

export interface TileModalFormValues {
  readonly type: TileType;
  readonly numberOfMetrics: NumberOfMetrics;
  readonly metricId: string;
  readonly metric2Id?: string;
  readonly alignAxes?: boolean;
  readonly name: string;
  readonly breakdowns: string[];
  readonly breakdownFilters: string[][];
  readonly filters: TileFilter[];
  readonly metric2Filters: TileFilter[];
}

type UnvalidatedFormValues = {
  readonly type: null;
  readonly numberOfMetrics: NumberOfMetrics;
  readonly metricId: null;
  readonly metric2Id: undefined;
  readonly alignAxes: undefined;
  readonly name: string;
  readonly breakdowns: string[];
  readonly breakdownFilters: string[][];
  readonly filters: TileFilter[];
  readonly metric2Filters: TileFilter[];
};

type TileModalFormUnvalidatedValues = {
  [key in keyof TileModalFormValues]:
    | TileModalFormValues[key]
    | UnvalidatedFormValues[key];
};

const DEFAULT_FORM_VALUES: TileModalFormUnvalidatedValues = {
  type: null,
  numberOfMetrics: NumberOfMetrics.ONE_METRIC,
  metricId: null,
  metric2Id: undefined,
  alignAxes: undefined,
  name: "",
  breakdowns: [],
  breakdownFilters: [],
  filters: [],
  metric2Filters: [],
};

const formValuesAreValidated = (
  values: TileModalFormUnvalidatedValues | TileModalFormValues,
): values is TileModalFormValues =>
  ((values as TileModalFormValues).type !== null &&
    (values as TileModalFormValues).numberOfMetrics ===
      NumberOfMetrics.ONE_METRIC &&
    (values as TileModalFormValues).metricId !== null) ||
  ((values as TileModalFormValues).numberOfMetrics ===
    NumberOfMetrics.TWO_METRICS &&
    (values as TileModalFormValues).metric2Id !== undefined);

const ModalSectionLabel = ({
  children,
  disabled,
}: {
  children: string;
  disabled?: boolean;
}) => {
  return (
    <label
      className={clsx("text-xs", "mb-0", "uppercase", {
        "text-v2-primary": disabled,
      })}
    >
      {children}
    </label>
  );
};

const ModalSection = ({
  children,
  disabled,
  label,
  className,
}: {
  children: ReactNode;
  disabled?: boolean;
  label: string;
  className?: string;
}): JSX.Element => (
  <div className={clsx("flex flex-col gap-1.5", className)}>
    <ModalSectionLabel disabled={disabled}>{label}</ModalSectionLabel>
    {children}
  </div>
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
  readonly title: string;
  readonly submitButtonText: string;
  readonly initialValues?: TileModalFormValues;
  readonly onSubmit: (
    values: Omit<TileModalFormValues, "numberOfMetrics">,
  ) => void;
}

export const TileModal = ({
  title,
  submitButtonText,
  initialValues,
  onSubmit,
  isOpen,
  onClose,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { metrics, labelsByLevel } = useOverviewContext();

  const [formValues, setFormValues] = useState<TileModalFormUnvalidatedValues>(
    initialValues === undefined ? DEFAULT_FORM_VALUES : initialValues,
  );

  const frequentlyUsedMetrics =
    formValues.type === TileType.CONVERSION_LAG
      ? getConversionLagMetrics(metrics)
      : getFrequentlyUsedMetrics(metrics);
  const otherMetrics =
    formValues.type === TileType.CONVERSION_LAG
      ? []
      : orderBy(getExcludedFrequentlyUsedMetrics(metrics), "name");

  const sensors = useSensors(useSensor(PointerSensor));

  const { value: breakdownConfigs } = useSalesBreakdownConfigs();

  const availableBreakdownConfigs = useMemo(
    () =>
      breakdownConfigs
        .filter((e) => !formValues.breakdowns.includes(e.key))
        .map((e) => e.key),
    [breakdownConfigs, formValues.breakdowns],
  );

  const isBreakdownDeactivated = useBreakdownDeactivatedCheck(
    initialValues?.breakdowns,
    initialValues?.filters,
    breakdownConfigs,
  );

  useEffect(() => {
    if (isBreakdownDeactivated) {
      setFormValues((prev) => ({
        ...prev,
        breakdowns: [],
        breakdownFilters: [],
        filters: [],
      }));
    }
  }, [isBreakdownDeactivated]);

  const topLevelFilters = useMemo(() => {
    const grouped = groupBy(formValues.filters, "breakdown");
    return Object.entries(grouped).map(([breakdown, values]) => ({
      key: breakdown,
      values: values.map((e) => e.value),
    }));
  }, [formValues.filters]);

  const metric2TopLevelFilters = useMemo(() => {
    const grouped = groupBy(formValues.metric2Filters, "breakdown");
    return Object.entries(grouped).map(([breakdown, values]) => ({
      key: breakdown,
      values: values.map((e) => e.value),
    }));
  }, [formValues.metric2Filters]);

  const maxAllowedLevelByBreakdown: Record<string, Level> = useMemo(() => {
    const result: Record<string, Level> = {};

    breakdownConfigs.forEach((option) => {
      let maxLevel = 0;

      option.choices.forEach((choice) => {
        const lvl =
          labelsByLevel[`${option.key}:${choice.value}`] ??
          labelsByLevel[`${option.key}:*`] ??
          3;
        const lvlNumber = LEVEL_TO_NUMBER[lvl];

        if (maxLevel < lvlNumber) {
          maxLevel = lvlNumber;
        }
      });

      result[option.key] = NUMBER_TO_LEVEL[maxLevel];
    });

    return result;
  }, [breakdownConfigs]);

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = formValues.breakdowns.findIndex((e) => e === active.id);
      const newIndex = formValues.breakdowns.findIndex((e) => e === over?.id);

      const newOrdering = arrayMove(formValues.breakdowns, oldIndex, newIndex);

      prettyLogEventPage({
        domain: "Tile modal",
        action: "onChange",
        additionalActionInfo: "Reorder breakdowns",
        data: {
          breakdowns: formValues.breakdowns,
          pageContext: { breakdowns: newOrdering },
        },
      });

      setFormValues((curr) => ({
        ...curr,
        breakdowns: newOrdering,
        breakdownFilters: [],
      }));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <h3 className="heading text-v2-primary mx-0 mb-0 mt-0 font-bold text-2xl">
          {title}
        </h3>
      }
    >
      <div className="flex justify-between mb-8">
        {Object.values(TileType).map((tileType) => (
          <TileTypeButton
            key={tileType}
            isSelected={formValues.type === tileType}
            tileType={tileType}
            onClick={() => {
              prettyLogEventPage({
                domain: "Tile modal",
                action: "onClick",
                additionalActionInfo: "Select metric",
              });
              setFormValues((curr) => ({
                ...curr,
                type: tileType,
                ...(tileType === TileType.METRIC
                  ? { alignAxes: undefined }
                  : null),
                ...(tileType === TileType.CONVERSION_LAG
                  ? {
                      metric2Id: undefined,
                      alignAxes: undefined,
                      breakdowns: [],
                      breakdownFilters: [],
                      metric2Filters: [],
                    }
                  : null),
                ...(tileType === TileType.CONVERSION_LAG &&
                !isConversionLagMetric(
                  metrics.find(({ id }) => id === curr.metricId),
                )
                  ? { metricId: null, filters: [] }
                  : null),
                numberOfMetrics:
                  tileType === TileType.CONVERSION_LAG
                    ? NumberOfMetrics.ONE_METRIC
                    : curr.numberOfMetrics,
              }));
            }}
          />
        ))}
      </div>
      {/* Add padding to the right of the div for the scroll bar. */}
      <div className="overflow-y-auto pr-[12px]">
        {isBreakdownDeactivated && (
          <div className="mb-4 p-0.5 text-xxs text-mars">
            <BreakdownsDeactivatedMessage />
            Options have been reset!
          </div>
        )}
        {formValues.type && (
          <div className="flex flex-col gap-[24px]">
            <NumberOfMetricsSwitch
              disabled={formValues.type === TileType.CONVERSION_LAG}
              value={formValues.numberOfMetrics}
              onChange={(numberOfMetrics) =>
                setFormValues((curr) => ({
                  ...curr,
                  numberOfMetrics,
                  metric2Id: undefined,
                  alignAxes: undefined,
                  metric2Filters: [],
                  ...(numberOfMetrics === NumberOfMetrics.TWO_METRICS
                    ? { breakdowns: [], breakdownFilters: [] }
                    : null),
                }))
              }
            />
            <ModalSection label="Name (Optional)">
              <input
                className={clsx(
                  "text-sm font-normal rounded-md w-full py-[5px] px-3 border bg-v2-primary",
                  "focus:outline-none focus:border-v2-action focus:ring-1 focus:ring-inset",
                  "focus:ring-cosmic disabled:bg-v2-primary border-v2-secondary",
                )}
                type="text"
                value={formValues.name}
                placeholder="Add your custom name…"
                onChange={(e) =>
                  setFormValues((curr) => ({ ...curr, name: e.target.value }))
                }
              />
            </ModalSection>
            <div className="flex gap-4">
              <ModalSection
                className="flex-1"
                label={
                  formValues.numberOfMetrics === NumberOfMetrics.ONE_METRIC
                    ? "Metric"
                    : "Metric 1"
                }
              >
                <SelectWithGroupedOptions
                  className={clsx(
                    "bg-v2-primary text-v2-primary",
                    "focus:outline-none focus:ring-cosmic focus:border-v2-action focus:ring-1",
                    "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
                  )}
                  menuClassName="z-[9998] max-h-[20rem]"
                  CustomSearchClass={CustomMetricSearch}
                  onChange={(metric) => {
                    prettyLogEventPage({
                      domain: "Tile modal",
                      action: "onChange",
                      additionalActionInfo: "Set metric",
                      data: {
                        pageContext: { metric },
                      },
                    });
                    setFormValues((curr) => ({
                      ...curr,
                      metricId: metric?.id || null,
                    }));
                  }}
                  isSearchable
                  value={metrics.find(({ id }) => id === formValues.metricId)}
                  groupedOptions={[
                    {
                      label: "Frequently Used",
                      options: frequentlyUsedMetrics.map((metric) => ({
                        label:
                          metric.metadata.overviewPageMetricName ||
                          metric.metadata.name ||
                          metric.name,
                        value: metric,
                      })),
                    },
                    {
                      options: otherMetrics.map((metric) => ({
                        label:
                          metric.metadata.overviewPageMetricName ||
                          metric.metadata.name ||
                          metric.name,
                        value: metric,
                      })),
                    },
                  ]}
                />
              </ModalSection>
              {formValues.numberOfMetrics === NumberOfMetrics.TWO_METRICS && (
                <ModalSection className="flex-1" label="Metric 2">
                  <SelectWithGroupedOptions
                    className={clsx(
                      "focus:outline-none focus:ring-cosmic focus:border-cosmic focus:ring-1",
                      "focus:ring-inset focus-within:outline-none focus-within:ring-inset",
                    )}
                    menuClassName="z-[9998] max-h-[20rem]"
                    CustomSearchClass={CustomMetricSearch}
                    onChange={(metric) => {
                      prettyLogEventPage({
                        domain: "Tile modal",
                        action: "onChange",
                        additionalActionInfo: "Set metric 2",
                        data: {
                          pageContext: { metric },
                        },
                      });
                      setFormValues((curr) => ({
                        ...curr,
                        metric2Id: metric?.id,
                      }));
                    }}
                    isSearchable
                    value={metrics.find(
                      ({ id }) => id === formValues.metric2Id,
                    )}
                    groupedOptions={[
                      {
                        label: "Frequently Used",
                        options: frequentlyUsedMetrics.map((metric) => ({
                          label:
                            metric.metadata.overviewPageMetricName ||
                            metric.metadata.name ||
                            metric.name,
                          value: metric,
                        })),
                      },
                      {
                        options: otherMetrics.map((metric) => ({
                          label:
                            metric.metadata.overviewPageMetricName ||
                            metric.metadata.name ||
                            metric.name,
                          value: metric,
                        })),
                      },
                    ]}
                  />
                </ModalSection>
              )}
            </div>
            {formValues.type !== TileType.CONVERSION_LAG &&
              formValues.numberOfMetrics === NumberOfMetrics.ONE_METRIC && (
                <ModalSection
                  label="Breakdown By (Optional)"
                  disabled={!formValues.metricId}
                >
                  {formValues.breakdowns.length !== 0 && (
                    <div className="flex flex-col space-y-2">
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        modifiers={[restrictToParentElement]}
                        onDragEnd={handleDragEnd}
                        autoScroll={false}
                      >
                        <SortableContext items={formValues.breakdowns}>
                          {formValues.breakdowns.map((breakdown, i) => (
                            <BreakdownSelect
                              key={breakdown}
                              options={breakdownConfigs
                                .filter(
                                  (e) =>
                                    !formValues.breakdowns.includes(e.key) ||
                                    e.key === breakdown,
                                )
                                .map((e) => e.key)}
                              value={breakdown}
                              position={i + 1}
                              showPosition={formValues.breakdowns.length > 1}
                              maxAllowedLevelByBreakdown={
                                maxAllowedLevelByBreakdown
                              }
                              onChange={(value) => {
                                prettyLogEventPage({
                                  domain: "Tile modal",
                                  action: "onChange",
                                  additionalActionInfo: "Update breakdown",
                                  data: {
                                    pageContext: { breakdown: value },
                                  },
                                });
                                setFormValues((curr) => ({
                                  ...curr,
                                  breakdownFilters: [],
                                  breakdowns: curr.breakdowns.map((e) =>
                                    e === breakdown ? value : e,
                                  ),
                                }));
                              }}
                              onDelete={() => {
                                prettyLogEventPage({
                                  domain: "Tile modal",
                                  action: "onChange",
                                  additionalActionInfo: "Delete breakdown",
                                  data: {
                                    pageContext: { breakdown },
                                  },
                                });
                                setFormValues((curr) => ({
                                  ...curr,
                                  breakdownFilters: [],
                                  breakdowns: curr.breakdowns.filter(
                                    (e) => e !== breakdown,
                                  ),
                                }));
                              }}
                            />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                  )}
                  {availableBreakdownConfigs.length !== 0 && (
                    <BreakdownSelect
                      disabled={!formValues.metricId}
                      options={availableBreakdownConfigs}
                      maxAllowedLevelByBreakdown={maxAllowedLevelByBreakdown}
                      placeholder={
                        formValues.breakdowns.length > 0
                          ? `${ordinal(
                              formValues.breakdowns.length + 1,
                            )} breakdown...`
                          : "Select..."
                      }
                      onChange={(value) => {
                        prettyLogEventPage({
                          domain: "Tile modal",
                          action: "onChange",
                          additionalActionInfo: "Change breakdowns",
                          data: {
                            pageContext: { breakdown: value },
                          },
                        });
                        setFormValues((curr) => ({
                          ...curr,
                          breakdownFilters: [],
                          breakdowns: [...curr.breakdowns, value],
                        }));
                      }}
                    />
                  )}
                  {formValues.breakdowns.length !== 0 && (
                    <BreakdownFilter
                      className="mt-3"
                      options={breakdownConfigs.map((e) => ({
                        key: e.key,
                        values: e.choices.map((e) => e.value),
                      }))}
                      breakdownKeys={formValues.breakdowns}
                      breakdownValues={formValues.breakdownFilters}
                      selectionLimit={5}
                      labelsByLevel={labelsByLevel}
                      onChange={(values) => {
                        prettyLogEventPage({
                          domain: "Tile modal",
                          action: "onChange",
                          additionalActionInfo: "Set breakdown filters",
                          data: {
                            pageContext: { breakdownFilters: values },
                          },
                        });
                        setFormValues((curr) => ({
                          ...curr,
                          breakdownFilters: values,
                        }));
                      }}
                    />
                  )}
                </ModalSection>
              )}
            <div className="flex gap-4">
              <div className="flex-1">
                <ModalSection
                  label={
                    formValues.numberOfMetrics === NumberOfMetrics.TWO_METRICS
                      ? "Metric 1 Filters (Optional)"
                      : "Filters (Optional)"
                  }
                  disabled={!formValues.metricId}
                >
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {topLevelFilters.map((e) => (
                      <FilterButtonActive
                        key={e.key}
                        breakdown={e}
                        onDelete={() => {
                          prettyLogEventPage({
                            domain: "Tile modal",
                            action: "onClick",
                            additionalActionInfo: "Delete filters",
                            data: {
                              pageContext: { filter: e.key },
                            },
                          });
                          setFormValues((curr) => ({
                            ...curr,
                            filters: curr.filters.filter(
                              (v) => v.breakdown !== e.key,
                            ),
                          }));
                        }}
                      />
                    ))}
                    <FilterButtonSelect
                      disabled={!formValues.metricId}
                      breakdownConfigs={breakdownConfigs}
                      breakdowns={topLevelFilters}
                      labelsByLevel={labelsByLevel}
                      onChange={(values) => {
                        prettyLogEventPage({
                          domain: "Tile modal",
                          action: "onChange",
                          additionalActionInfo: "Change filters",
                          data: {
                            pageContext: { filters: values },
                          },
                        });
                        setFormValues((curr) => ({
                          ...curr,
                          filters: values.flatMap((breakdown) =>
                            breakdown.values.map((value) => ({
                              breakdown: breakdown.key,
                              value,
                            })),
                          ),
                        }));
                      }}
                    />
                  </div>
                </ModalSection>
              </div>
              {formValues.numberOfMetrics === NumberOfMetrics.TWO_METRICS && (
                <div className="flex-1">
                  <ModalSection
                    label="Metric 2 Filters (Optional)"
                    disabled={!formValues.metric2Id}
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {metric2TopLevelFilters.map((e) => (
                        <FilterButtonActive
                          key={e.key}
                          breakdown={e}
                          onDelete={() => {
                            prettyLogEventPage({
                              domain: "Tile modal",
                              action: "onClick",
                              additionalActionInfo: "Delete filters",
                              data: {
                                pageContext: { filter: e.key },
                              },
                            });
                            setFormValues((curr) => ({
                              ...curr,
                              metric2Filters: curr.metric2Filters.filter(
                                (v) => v.breakdown !== e.key,
                              ),
                            }));
                          }}
                        />
                      ))}
                      <FilterButtonSelect
                        disabled={!formValues.metric2Id}
                        breakdownConfigs={breakdownConfigs}
                        breakdowns={metric2TopLevelFilters}
                        labelsByLevel={labelsByLevel}
                        onChange={(values) => {
                          prettyLogEventPage({
                            domain: "Tile modal",
                            action: "onChange",
                            additionalActionInfo: "Change filters",
                            data: {
                              pageContext: { filters: values },
                            },
                          });
                          setFormValues((curr) => ({
                            ...curr,
                            metric2Filters: values.flatMap((breakdown) =>
                              breakdown.values.map((value) => ({
                                breakdown: breakdown.key,
                                value,
                              })),
                            ),
                          }));
                        }}
                      />
                    </div>
                  </ModalSection>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-8">
        <TertiaryButton onClick={onClose}>CANCEL</TertiaryButton>
        <PrimaryButton
          className="uppercase"
          disabled={
            !formValues.type ||
            !formValues.metricId ||
            (formValues.numberOfMetrics === NumberOfMetrics.TWO_METRICS &&
              !formValues.metric2Id) ||
            (formValues.breakdowns.length > 0 &&
              formValues.breakdownFilters.length === 0)
          }
          onClick={() => {
            if (initialValues && isEqual(initialValues, formValues)) {
              onClose();
              return;
            }

            prettyLogEventPage({
              domain: "Tile modal",
              action: "onSubmit",
              additionalActionInfo: "Submit Tile configuration",
            });
            if (formValuesAreValidated(formValues)) {
              const { numberOfMetrics, ...tileValues } = formValues;
              onSubmit(tileValues);
            }
          }}
        >
          {submitButtonText}
        </PrimaryButton>
      </div>
    </Modal>
  );
};
